import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { startAudit, startChannelAudit, startBulkAudit, checkBulkJobStatus, checkJobStatus, cancelJob } from '../services/engineService';
import '../styles/EngineGlassmorphism.css';

export default function VideoAuditDashboard({ user, onCreditsUpdate }) {
  const [auditMode, setAuditMode] = useState('single'); // 'single' | 'bulk'
  const [videoId, setVideoId] = useState('');
  const [jobId, setJobId] = useState(null);
  const [jobState, setJobState] = useState(null); // The raw job object from API
  const [dashboardData, setDashboardData] = useState(null);
  const [error, setError] = useState(null);
  const location = useLocation();

  // Bulk Audit States (Phase 1.3)
  const [bulkInput, setBulkInput] = useState('');
  const [bulkJobIds, setBulkJobIds] = useState([]);
  const [bulkResults, setBulkResults] = useState([]);
  const [bulkLoading, setBulkLoading] = useState(false);

  // On Mount: Check if jobId is passed via URL (from History tab)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const urlJobId = params.get('jobId');
    if (urlJobId) {
       setJobId(urlJobId);
       setJobState({ status: 'fetching', stage: 'Loading Report...' });
       checkJobStatus(urlJobId).then(res => {
          setJobState(res.job);
          if (res.job.status === 'completed') {
            setDashboardData(res.data);
          } else if (res.job.status === 'failed' || res.job.status === 'cancelled') {
            setError(res.job.error || 'Job failed to complete.');
          }
       }).catch(err => setError(err.message));
    }
  }, [location.search]);

  // Polling Effect for Single Job
  useEffect(() => {
    let pollTimer;
    if (jobId && jobState?.status !== 'completed' && jobState?.status !== 'failed' && jobState?.status !== 'cancelled') {
      pollTimer = setInterval(async () => {
        try {
          const res = await checkJobStatus(jobId);
          setJobState(res.job);
          if (res.job.status === 'completed') {
            setDashboardData(res.data);
            clearInterval(pollTimer);
          } else if (res.job.status === 'failed' || res.job.status === 'cancelled') {
            setError(res.job.error || 'Job failed to complete.');
            clearInterval(pollTimer);
          }
        } catch (err) {
          setError(err.message);
          setJobState({ status: 'failed', error: err.message });
          clearInterval(pollTimer);
        }
      }, 3000);
    }
    return () => clearInterval(pollTimer);
  }, [jobId, jobState]);

  // Polling Effect for Bulk Jobs (Phase 1.3)
  useEffect(() => {
    let timer;
    if (bulkJobIds.length > 0) {
      timer = setInterval(async () => {
        try {
          const res = await checkBulkJobStatus(bulkJobIds);
          if (res.success && res.jobs) {
            setBulkResults(res.jobs);
            const allFinished = res.jobs.every(j => ['completed', 'failed', 'cancelled'].includes(j.status));
            if (allFinished) {
              setBulkLoading(false);
              clearInterval(timer);
            }
          }
        } catch (e) {
          console.error('Bulk poll error:', e);
        }
      }, 3000);
    }
    return () => clearInterval(timer);
  }, [bulkJobIds]);

  const handleStartAudit = async () => {
    setError(null);
    setDashboardData(null);
    setJobState(null);
    
    const trimmed = videoId.trim();
    if (!trimmed) {
      setError('Please enter a YouTube video URL, video ID, or channel handle');
      return;
    }

    const isChannelInput = trimmed.startsWith('@') ||
                           trimmed.includes('youtube.com/channel/') ||
                           trimmed.includes('youtube.com/@') ||
                           (trimmed.startsWith('UC') && trimmed.length === 24);

    try {
      let res;
      if (isChannelInput) {
        res = await startChannelAudit(trimmed);
      } else {
        let idToSend = trimmed;
        if (trimmed.includes('v=')) {
          try { idToSend = new URL(trimmed.startsWith('http') ? trimmed : `https://${trimmed}`).searchParams.get('v') || trimmed; } catch(e) {}
        } else if (trimmed.includes('youtu.be/')) {
          idToSend = trimmed.split('youtu.be/')[1].split('?')[0];
        }
        res = await startAudit(idToSend);
      }

      setJobId(res.jobId);
      if (res.creditsRemaining !== undefined && onCreditsUpdate) {
        onCreditsUpdate(res.creditsRemaining);
      }
      setJobState({ status: 'queued', progress: 0, stage: 'Queued' });
    } catch (err) {
      setError(err.message);
    }
  };

  const handleStartBulkAudit = async () => {
    setError(null);
    setBulkResults([]);
    
    const rawLines = bulkInput.split(/[\n,]+/).map(s => s.trim()).filter(Boolean);
    if (rawLines.length === 0) {
      setError('Please enter at least 1 video URL or Video ID for bulk audit.');
      return;
    }

    const cleanIds = rawLines.map(trimmed => {
      if (trimmed.includes('v=')) {
        try { return new URL(trimmed.startsWith('http') ? trimmed : `https://${trimmed}`).searchParams.get('v') || trimmed; } catch(e) {}
      } else if (trimmed.includes('youtu.be/')) {
        return trimmed.split('youtu.be/')[1].split('?')[0];
      }
      return trimmed;
    }).slice(0, 10);

    setBulkLoading(true);
    try {
      const res = await startBulkAudit(cleanIds);
      if (res.success && res.jobIds) {
        setBulkJobIds(res.jobIds);
        if (res.creditsRemaining !== undefined && onCreditsUpdate) {
          onCreditsUpdate(res.creditsRemaining);
        }
      } else {
        setError('Bulk audit launch failed');
        setBulkLoading(false);
      }
    } catch (err) {
      setError(err.message || 'Bulk audit failed');
      setBulkLoading(false);
    }
  };

  const handleCancel = async () => {
    if (jobId) {
      try {
        await cancelJob(jobId);
        setJobState(prev => ({ ...prev, status: 'cancelled', stage: 'Cancelled by user' }));
      } catch(e) {
        console.error(e);
      }
    }
  };

  const isProcessing = jobState && ['queued', 'pending', 'processing', 'retrying'].includes(jobState.status);

  const handleDownloadJSON = () => {
    if (!dashboardData) return;
    const dataStr = "data:text/json;charset=utf-8," + encodeURIComponent(JSON.stringify(dashboardData, null, 2));
    const downloadAnchorNode = document.createElement('a');
    downloadAnchorNode.setAttribute("href",     dataStr);
    downloadAnchorNode.setAttribute("download", "video_audit_report.json");
    document.body.appendChild(downloadAnchorNode);
    downloadAnchorNode.click();
    downloadAnchorNode.remove();
  };

  const [exportingPDF, setExportingPDF] = useState(false);

  const handleExportPDF = async () => {
    if (!dashboardData) return;
    setExportingPDF(true);
    try {
      const token = localStorage.getItem('authToken');
      const res = await fetch('/api/export/pdf', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          keyword: videoId || 'video-audit',
          data: dashboardData
        })
      });

      if (!res.ok) throw new Error('PDF export failed');
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `video_audit_report_${videoId || 'export'}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      console.error(e);
      alert('Failed to generate PDF: ' + e.message);
    } finally {
      setExportingPDF(false);
    }
  };

  return (
    <div className="engine-dashboard">
      <header className="engine-header" style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
        <div>
          <h1>✨ AI Video Audit</h1>
          <p>Production-grade YouTube Analysis Engine (Phase C0 & Phase 1)</p>
        </div>
        {dashboardData && (
          <div style={{display: 'flex', gap: '1rem'}}>
            <button className="engine-btn" style={{background: '#3b82f6'}} onClick={handleExportPDF} disabled={exportingPDF}>
              {exportingPDF ? '⏳ Generating PDF...' : '📄 Export PDF'}
            </button>
            <button className="engine-btn" style={{background: '#10b981'}} onClick={handleDownloadJSON}>Export JSON</button>
          </div>
        )}
      </header>

      {/* Mode Switcher */}
      <div className="engine-glass-panel" style={{ marginBottom: '1.5rem' }}>
        <div style={{ display: 'flex', gap: '1rem', marginBottom: '1rem' }}>
          <button 
            onClick={() => setAuditMode('single')}
            style={{
              padding: '0.5rem 1.2rem',
              borderRadius: '8px',
              border: 'none',
              cursor: 'pointer',
              fontWeight: 600,
              background: auditMode === 'single' ? 'linear-gradient(90deg, #8b5cf6, #ec4899)' : 'rgba(255,255,255,0.05)',
              color: '#fff'
            }}
          >
            🎬 Single / Channel Audit
          </button>
          <button 
            onClick={() => setAuditMode('bulk')}
            style={{
              padding: '0.5rem 1.2rem',
              borderRadius: '8px',
              border: 'none',
              cursor: 'pointer',
              fontWeight: 600,
              background: auditMode === 'bulk' ? 'linear-gradient(90deg, #8b5cf6, #ec4899)' : 'rgba(255,255,255,0.05)',
              color: '#fff'
            }}
          >
            📦 Bulk Video Matrix (Up to 10 Videos)
          </button>
        </div>

        {auditMode === 'single' ? (
          <div className="engine-input-group">
            <input 
              type="text" 
              className="engine-input" 
              placeholder="Paste YouTube URL, Video ID, or @handle here..." 
              value={videoId}
              onChange={(e) => setVideoId(e.target.value)}
              disabled={isProcessing}
            />
            <button 
              className="engine-btn" 
              onClick={handleStartAudit} 
              disabled={!videoId || isProcessing}
            >
              {isProcessing ? 'Analyzing...' : 'Audit Video'}
            </button>
            {isProcessing && (
              <button className="engine-btn" style={{background: '#475569'}} onClick={handleCancel}>
                Cancel
              </button>
            )}
          </div>
        ) : (
          <div>
            <p style={{ color: '#94a3b8', fontSize: '0.85rem', marginBottom: '0.5rem' }}>
              Paste up to 10 YouTube Video URLs or Video IDs (one per line or comma-separated). 💳 <strong>40 Credits</strong> per bulk audit run.
            </p>
            <textarea
              className="engine-input"
              rows={4}
              placeholder={`https://www.youtube.com/watch?v=...\nhttps://www.youtube.com/watch?v=...`}
              value={bulkInput}
              onChange={(e) => setBulkInput(e.target.value)}
              disabled={bulkLoading}
              style={{ width: '100%', marginBottom: '1rem', resize: 'vertical', fontFamily: 'monospace', borderRadius: '8px', padding: '0.8rem' }}
            />
            <button 
              className="engine-btn" 
              onClick={handleStartBulkAudit}
              disabled={bulkLoading || !bulkInput.trim()}
              style={{ background: 'linear-gradient(90deg, #8b5cf6, #ec4899)' }}
            >
              {bulkLoading ? '⏳ Processing Batch Queue...' : '🚀 Start Bulk Audit (40 Credits)'}
            </button>
          </div>
        )}

        {error && <div className="engine-error" style={{ marginTop: '1rem' }}>{error}</div>}
      </div>

      {/* Bulk Results Matrix Table (Phase 1.3) */}
      {bulkResults.length > 0 && (
        <div className="engine-glass-panel" style={{ marginBottom: '2rem' }}>
          <h3>📊 Bulk Video Audit Matrix ({bulkResults.length} Videos)</h3>
          <p style={{ color: '#94a3b8', fontSize: '0.85rem', marginBottom: '1rem' }}>
            Comparative performance matrix for batch audit queue.
          </p>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', textAlign: 'left', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
                  <th style={{ padding: '0.8rem' }}>Video ID</th>
                  <th style={{ padding: '0.8rem' }}>Status</th>
                  <th style={{ padding: '0.8rem' }}>Overall</th>
                  <th style={{ padding: '0.8rem' }}>SEO</th>
                  <th style={{ padding: '0.8rem' }}>Engagement</th>
                  <th style={{ padding: '0.8rem' }}>Quality</th>
                  <th style={{ padding: '0.8rem' }}>Action</th>
                </tr>
              </thead>
              <tbody>
                {bulkResults.map((item) => {
                  const dto = item.data?.deterministic?.summary || item.data?.summary || {};
                  return (
                    <tr key={item.id} style={{ borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                      <td style={{ padding: '0.8rem', fontWeight: 600, color: '#f8fafc' }}>{item.target_id}</td>
                      <td style={{ padding: '0.8rem' }}>
                        <span className="ai-badge" style={{ background: item.status === 'completed' ? 'rgba(16,185,129,0.2)' : 'rgba(245,158,11,0.2)' }}>
                          {item.status} ({item.progress || 0}%)
                        </span>
                      </td>
                      <td style={{ padding: '0.8rem', fontWeight: 700, color: '#60a5fa' }}>{dto.overallScore ?? '-'}</td>
                      <td style={{ padding: '0.8rem', color: '#34d399' }}>{dto.seoScore ?? '-'}</td>
                      <td style={{ padding: '0.8rem', color: '#f472b6' }}>{dto.engagementScore ?? '-'}</td>
                      <td style={{ padding: '0.8rem', color: '#c084fc' }}>{dto.contentQualityScore ?? '-'}</td>
                      <td style={{ padding: '0.8rem' }}>
                        {item.data && (
                          <button 
                            className="engine-btn" 
                            style={{ padding: '0.3rem 0.8rem', fontSize: '0.8rem', background: '#3b82f6' }}
                            onClick={() => { setDashboardData(item.data); setAuditMode('single'); }}
                          >
                            View Full Report
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Loading State Overlay */}
      {isProcessing && (
        <div className="engine-glass-panel engine-loading-container">
          <h2>Analyzing Content...</h2>
          <div className="engine-progress-bar-bg">
            <div className="engine-progress-bar-fill" style={{ width: `${jobState.progress || 0}%` }}></div>
          </div>
          <p className="engine-loading-text">{jobState.stage || 'Initializing...'}</p>
          {jobState.status === 'retrying' && (
             <p style={{color: '#f59e0b', marginTop: '1rem'}}>AI Provider Timeout. Retrying graceful fallback...</p>
          )}
        </div>
      )}

      {/* Channel Results Dashboard (Phase 1.1) */}
      {dashboardData && dashboardData.targetType === 'channel' && (
        <div className="engine-results">
          {/* Channel Header Banner */}
          <div className="engine-glass-panel" style={{ display: 'flex', alignItems: 'center', gap: '1.5rem', flexWrap: 'wrap' }}>
            {dashboardData.channelInfo?.thumbnails?.default?.url && (
              <img 
                src={dashboardData.channelInfo.thumbnails.default.url} 
                alt={dashboardData.channelInfo.title} 
                style={{ width: '80px', height: '80px', borderRadius: '50%', border: '2px solid #8b5cf6' }} 
              />
            )}
            <div style={{ flex: 1 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.8rem' }}>
                <h2 style={{ margin: 0, fontSize: '1.6rem', color: '#f8fafc' }}>{dashboardData.channelInfo?.title}</h2>
                <span style={{ background: 'rgba(139,92,246,0.2)', color: '#c084fc', padding: '0.2rem 0.6rem', borderRadius: '8px', fontSize: '0.8rem' }}>
                  Channel Audit
                </span>
              </div>
              <p style={{ margin: '0.4rem 0 0 0', color: '#94a3b8', fontSize: '0.9rem' }}>
                👥 {Number(dashboardData.channelInfo?.subscriberCount || 0).toLocaleString()} Subscribers • 🎬 {Number(dashboardData.channelInfo?.videoCount || 0).toLocaleString()} Total Videos • 👁️ {Number(dashboardData.channelInfo?.viewCount || 0).toLocaleString()} Views
              </p>
            </div>
          </div>

          {/* Channel Composite Scores Grid */}
          <div className="engine-scores-grid">
            <div className="engine-glass-panel score-card">
              <h3>Overall Channel Score</h3>
              <div className={`score-value ${getScoreColor(dashboardData.compositeScores?.overallScore)}`}>
                {dashboardData.compositeScores?.overallScore || 0}
              </div>
              <p style={{ fontSize: '0.75rem', color: '#94a3b8', marginTop: '0.3rem' }}>
                (Avg + Consistency Bonus: +{dashboardData.compositeScores?.consistencyBonus || 0})
              </p>
            </div>

            <div className="engine-glass-panel score-card">
              <h3>Avg Video SEO</h3>
              <div className={`score-value ${getScoreColor(dashboardData.compositeScores?.seoScore)}`}>
                {dashboardData.compositeScores?.seoScore || 0}
              </div>
            </div>

            <div className="engine-glass-panel score-card">
              <h3>Avg Engagement</h3>
              <div className={`score-value ${getScoreColor(dashboardData.compositeScores?.engagementScore)}`}>
                {dashboardData.compositeScores?.engagementScore || 0}
              </div>
            </div>

            <div className="engine-glass-panel score-card">
              <h3>Upload Pace</h3>
              <div className="score-value score-med" style={{ fontSize: '1.8rem' }}>
                {dashboardData.compositeScores?.uploadFrequency || 0}/mo
              </div>
            </div>
          </div>

          {/* Recent Analyzed Videos List */}
          {dashboardData.videoAudits && dashboardData.videoAudits.length > 0 && (
            <div className="engine-glass-panel">
              <h3>📹 Top Channel Recent Videos Analyzed ({dashboardData.videoAudits.length})</h3>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '1rem', marginTop: '1rem' }}>
                {dashboardData.videoAudits.map((vid, idx) => (
                  <div key={idx} style={{ background: 'rgba(0,0,0,0.3)', padding: '1rem', borderRadius: '12px', border: '1px solid rgba(255,255,255,0.06)' }}>
                    <strong style={{ color: '#e2e8f0', fontSize: '0.95rem', display: 'block', marginBottom: '0.5rem' }}>{vid.title}</strong>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.85rem', color: '#94a3b8' }}>
                      <span>👁️ {Number(vid.views || 0).toLocaleString()} views</span>
                      <span className={`score-value ${getScoreColor(vid.overallScore)}`} style={{ fontSize: '1rem' }}>Score: {vid.overallScore}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* AI Channel Strategy Insights */}
          {dashboardData.ai && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '1.5rem', marginTop: '1rem' }}>
              {dashboardData.ai.executiveSummary && (
                <div className="engine-glass-panel">
                  <span className="ai-badge">✨ Channel Executive Strategy</span>
                  <h4 style={{ color: '#c084fc', marginTop: '0.5rem', fontSize: '1.1rem' }}>{dashboardData.ai.executiveSummary.headline}</h4>
                  <p style={{ fontSize: '1rem', color: '#cbd5e1', marginTop: '0.5rem', lineHeight: '1.5' }}>
                    {dashboardData.ai.executiveSummary.summary}
                  </p>
                </div>
              )}

              {dashboardData.ai.channelSEOStrategy && Array.isArray(dashboardData.ai.channelSEOStrategy) && (
                <div className="engine-glass-panel">
                  <span className="ai-badge" style={{ background: 'rgba(16, 185, 129, 0.2)', color: '#6ee7b7' }}>🎯 Channel SEO Strategy</span>
                  <ul className="ai-recommendation-list" style={{ marginTop: '0.5rem' }}>
                    {dashboardData.ai.channelSEOStrategy.map((strat, idx) => (
                      <li key={idx}><span>{strat}</span></li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Single Video Results Dashboard */}
      {dashboardData && dashboardData.deterministic && (
        <div className="engine-results">
          
          {/* Main 4 Scores Grid */}
          <div className="engine-scores-grid">
            <div className="engine-glass-panel score-card">
              <h3>Overall Score</h3>
              <div className={`score-value ${getScoreColor(dashboardData.deterministic.summary.overallScore)}`}>
                {(dashboardData.deterministic.summary.overallScore || 0).toFixed(0)}
              </div>
            </div>
            <div className="engine-glass-panel score-card">
              <h3>SEO Score</h3>
              <div className={`score-value ${getScoreColor(dashboardData.deterministic.summary.seoScore)}`}>
                {(dashboardData.deterministic.summary.seoScore || 0).toFixed(0)}
              </div>
            </div>
            <div className="engine-glass-panel score-card">
              <h3>Engagement Score</h3>
              <div className={`score-value ${getScoreColor(dashboardData.deterministic.summary.engagementScore)}`}>
                {(dashboardData.deterministic.summary.engagementScore || 0).toFixed(0)}
              </div>
            </div>
            <div className="engine-glass-panel score-card">
              <h3>Content Quality</h3>
              <div className={`score-value ${getScoreColor(dashboardData.deterministic.summary.contentQualityScore)}`}>
                {(dashboardData.deterministic.summary.contentQualityScore || 0).toFixed(0)}
              </div>
            </div>
          </div>

          {/* Module Performance Breakdown */}
          {dashboardData.deterministic.modules && Object.keys(dashboardData.deterministic.modules).length > 0 && (
            <div className="engine-glass-panel">
              <h3>📊 Module Score Breakdown</h3>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '1rem', marginTop: '1rem' }}>
                {Object.entries(dashboardData.deterministic.modules).map(([modKey, modVal]) => (
                  <div key={modKey} style={{ background: 'rgba(0,0,0,0.2)', padding: '1rem', borderRadius: '12px', textAlign: 'center' }}>
                    <span style={{ textTransform: 'capitalize', color: '#94a3b8', fontSize: '0.9rem' }}>{modKey}</span>
                    <div className={`score-value ${getScoreColor(modVal.score)}`} style={{ fontSize: '1.8rem', margin: '0.5rem 0' }}>
                      {modVal.score}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Flagged Action Items / Issues */}
          {dashboardData.deterministic.actionItems && dashboardData.deterministic.actionItems.length > 0 && (
            <div className="engine-glass-panel">
              <h3>⚠️ Flagged Optimization Issues ({dashboardData.deterministic.actionItems.length})</h3>
              <ul className="ai-recommendation-list" style={{ marginTop: '1rem' }}>
                {dashboardData.deterministic.actionItems.map((item, idx) => (
                  <li key={idx} style={{ borderLeftColor: '#ef4444' }}>
                    <strong style={{ color: '#f87171', textTransform: 'uppercase', fontSize: '0.8rem', marginRight: '0.5rem' }}>[{item.module}]</strong>
                    <span>{item.text}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* AI Insights Section */}
          {dashboardData.ai && (
            <>
              {/* AI Executive Summary & SEO Coach */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '1.5rem' }}>
                {dashboardData.ai.executiveSummary && (
                  <div className="engine-glass-panel">
                    <span className="ai-badge">✨ AI Executive Summary</span>
                    <p style={{fontSize: '1.1rem', lineHeight: '1.6', marginTop: '0.5rem'}}>
                      {dashboardData.ai.executiveSummary?.text || dashboardData.ai.executiveSummary?.error || 'Summary unavailable.'}
                    </p>
                  </div>
                )}

                {dashboardData.ai.seoCoach && (
                  <div className="engine-glass-panel">
                    <span className="ai-badge" style={{ background: 'rgba(16, 185, 129, 0.2)', color: '#6ee7b7' }}>🎯 SEO Coach Advice</span>
                    <p style={{fontSize: '1.1rem', lineHeight: '1.6', marginTop: '0.5rem'}}>
                      {dashboardData.ai.seoCoach?.message || 'Coaching advice unavailable.'}
                    </p>
                  </div>
                )}
              </div>

              {/* Priority Fixes */}
              {dashboardData.ai.priorityFixes && (
                <div className="engine-glass-panel">
                  <span className="ai-badge">🔥 Priority Fixes</span>
                  {dashboardData.ai.priorityFixes?.error ? (
                     <p className="engine-partial-failure">⚠️ {dashboardData.ai.priorityFixes.error}</p>
                  ) : (
                    <ul className="ai-recommendation-list">
                      {dashboardData.ai.priorityFixes?.fixes?.map((fix, idx) => (
                        <li key={idx}>
                          <strong style={{ color: fix.priority === 'High' ? '#ef4444' : '#f59e0b', marginRight: '0.5rem' }}>[{fix.priority}]</strong> 
                          <span>{fix.text}</span>
                          {fix.supportingMetric && (
                            <p style={{fontSize: '0.85rem', color: '#94a3b8', marginTop: '0.3rem'}}>Impacted Metric: {fix.supportingMetric}</p>
                          )}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}

              {/* Optimized Titles */}
              {dashboardData.ai.titles && (
                <div className="engine-glass-panel">
                  <span className="ai-badge">✍️ Optimized Titles</span>
                  {dashboardData.ai.titles?.error ? (
                     <p className="engine-partial-failure">⚠️ {dashboardData.ai.titles.error}</p>
                  ) : (
                    <ul className="ai-recommendation-list">
                      {dashboardData.ai.titles?.candidates?.map((t, idx) => (
                        <li key={idx}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <strong style={{ fontSize: '1.1rem', color: '#60a5fa' }}>{t.text}</strong>
                            <span style={{ background: 'rgba(59, 130, 246, 0.2)', color: '#93c5fd', padding: '0.2rem 0.6rem', borderRadius: '8px', fontSize: '0.8rem' }}>
                              CTR: {t.expectedCTR}
                            </span>
                          </div>
                          <p style={{fontSize: '0.9rem', color: '#94a3b8', marginTop: '0.5rem'}}>{t.explanation}</p>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}

              {/* Description Suggestions */}
              {dashboardData.ai.descriptions && dashboardData.ai.descriptions.candidates && (
                <div className="engine-glass-panel">
                  <span className="ai-badge">📝 Optimized Description Candidates</span>
                  <ul className="ai-recommendation-list">
                    {dashboardData.ai.descriptions.candidates.map((d, idx) => (
                      <li key={idx}>
                        <p style={{ whiteSpace: 'pre-line', fontSize: '1rem', lineHeight: '1.5', color: '#cbd5e1' }}>{d.text}</p>
                        <p style={{ fontSize: '0.85rem', color: '#94a3b8', marginTop: '0.5rem' }}>💡 {d.explanation}</p>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Strategic Tags */}
              {dashboardData.ai.tags && dashboardData.ai.tags.sets && (
                <div className="engine-glass-panel">
                  <span className="ai-badge">🏷️ Strategic Tag Sets</span>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '1rem', marginTop: '0.5rem' }}>
                    {dashboardData.ai.tags.sets.map((set, idx) => (
                      <div key={idx} style={{ background: 'rgba(0,0,0,0.2)', padding: '1rem', borderRadius: '12px' }}>
                        <strong style={{ color: '#ec4899', fontSize: '0.95rem' }}>Target Audience: {set.targetAudience}</strong>
                        <p style={{ fontSize: '0.9rem', color: '#cbd5e1', marginTop: '0.5rem', lineHeight: '1.4' }}>{set.text}</p>
                        <p style={{ fontSize: '0.8rem', color: '#94a3b8', marginTop: '0.5rem' }}>{set.explanation}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Thumbnail Psychology Concepts */}
              {dashboardData.ai.thumbnailConcepts && dashboardData.ai.thumbnailConcepts.concepts && (
                <div className="engine-glass-panel">
                  <span className="ai-badge">🖼️ Thumbnail Visual Psychology Concepts</span>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '1rem', marginTop: '0.5rem' }}>
                    {dashboardData.ai.thumbnailConcepts.concepts.map((concept, idx) => (
                      <div key={idx} style={{ background: 'rgba(0,0,0,0.2)', padding: '1rem', borderRadius: '12px' }}>
                        <span style={{ background: 'rgba(236, 72, 153, 0.2)', color: '#f472b6', padding: '0.2rem 0.6rem', borderRadius: '9999px', fontSize: '0.75rem', fontWeight: 'bold' }}>
                          Emotion: {concept.emotion}
                        </span>
                        <p style={{ fontSize: '0.95rem', color: '#e2e8f0', marginTop: '0.5rem', fontWeight: '500' }}>{concept.text}</p>
                        <p style={{ fontSize: '0.85rem', color: '#94a3b8', marginTop: '0.5rem' }}>{concept.explanation}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Execution Metrics Footer */}
              <div style={{marginTop: '2rem', textAlign: 'center', color: '#64748b', fontSize: '0.85rem'}}>
                 <p>Generated by {dashboardData.ai.titles?.metadata?.model || 'AI'} | Latency: {dashboardData.ai.titles?.metadata?.latency || 0}ms</p>
              </div>
            </>
          )}

        </div>
      )}
    </div>
  );
}

function getScoreColor(score) {
  if (score >= 80) return 'score-high';
  if (score >= 50) return 'score-med';
  return 'score-low';
}
